<template>
  <div class="bg-white mt-5 rounded p-3 px-5">
    <div class="flex items-center border-b mb-6 pb-2">
      <div
        class="w-24 h-24 lg:w-32 lg:h-32 p-4 -mt-12 mb-0 lg:mb-2 rounded-lg relative flex-no-shrink"
        :class="page.icon_color ? page.icon_color : 'bg-tertiary'"
      >
        <icon name="videoettes" class="text-white" />
      </div>
      <h3 class="mx-6 text-3xl font-medium">
        Automation Settings
      </h3>
    </div>

    <div class="w-full flex items-start">
      <div class="w-full justify-center flex">
        <template v-if="!sales.subscription && !lettings.subscription">
          <loader></loader>
        </template>
        <template v-else>
          <div class="w-full flex flex-col md:flex-row">
            <subscription @save="saveSubscription(sales)" v-model="sales" class="md:mr-4" />
            <subscription @save="saveSubscription(lettings)" v-model="lettings" type="Lettings" />
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/http'
import { CancelToken } from 'axios'
import Subscription from '@/components/Videoettes/Subscription'
import { mapState, mapActions } from 'vuex'
import ErrorBag from '@/ErrorBag'

export default {
  name: 'Settings',

  props: ['page'],

  components: {
    Subscription
  },

  computed: {
    ...mapState({
      selectedOffice: state => state.selectedOffice
    })
  },

  data () {
    return {
      sales: {
        subscription: null,
        req: null,
        loading: false,
        errors: new ErrorBag()
      },
      lettings: {
        subscription: null,
        req: null,
        loading: false,
        errors: new ErrorBag()
      }
    }
  },

  methods: {
    ...mapActions([
      'displayToastNotification'
    ]),

    getSubscription () {
      api
        .get(`/videoettes/subscriptions`, {
          params: { office_id: this.selectedOffice.id }
        })
        .then(response => {
          this.sales.subscription = response.data.sales
          this.lettings.subscription = response.data.lettings
        })
        .catch(() => {
          this.displayToastNotification('Unable to fetch your subscription settings, please try again later.')
        })
    },

    saveSubscription (sub) {
      if (sub.subscription.id) {
        this.updateSubscription(sub)
      } else {
        this.createSubscription(sub)
      }
    },

    createSubscription (sub) {
      sub.loading = true
      sub.errors.clear()

      if (sub.req) {
        sub.req.cancel()
      }

      sub.req = CancelToken.source()

      api
        .post(`/videoettes/subscriptions`, {
          ...sub.subscription, office_id: this.selectedOffice.id
        }, {
          cancelToken: sub.req.token
        })
        .then(response => {
          this.getSubscription()
          this.displayToastNotification({
            type: 'success',
            icon: 'fa-check',
            title: 'Subscription saved',
            info: `Your subscription has been saved.`
          })
        })
        .catch(({ response }) => {
          switch (response.status) {
            case 403:
              this.displayToastNotification({
                type: 'warning',
                icon: 'fa-bell',
                title: 'Unable to save subscription',
                info: `You don't have permission to save this subscription.`
              })
              return
            case 422:
              sub.errors.store(response.data)
              return
          }

          this.displayToastNotification({
            type: 'danger',
            icon: 'fa-bell',
            title: 'Unable to save subscription',
            info: `We've been unable to save your subscription, please try again later.`
          })
        })
        .finally(() => {
          sub.loading = false
        })
    },

    updateSubscription (sub) {
      sub.loading = true
      sub.errors.clear()

      if (sub.req) {
        sub.req.cancel()
      }

      sub.req = CancelToken.source()

      api
        .patch(`/videoettes/subscriptions/${sub.subscription.id}`, {
          ...sub.subscription, office_id: this.selectedOffice.id
        }, {
          cancelToken: sub.req.token
        })
        .then(response => {
          this.getSubscription()
          this.displayToastNotification({
            type: 'success',
            icon: 'fa-check',
            title: 'Subscription saved',
            info: `Your subscription has been saved.`
          })
        })
        .catch(({ response }) => {
          switch (response.status) {
            case 403:
              this.displayToastNotification({
                type: 'warning',
                icon: 'fa-bell',
                title: 'Unable to save subscription',
                info: `You don't have permission to save this subscription.`
              })
              return
            case 422:
              sub.errors.store(response.data)
              return
          }

          this.displayToastNotification({
            type: 'danger',
            icon: 'fa-bell',
            title: 'Unable to save subscription',
            info: `We've been unable to save your subscription, please try again later.`
          })
        })
        .finally(() => {
          sub.loading = false
        })
    },

    deleteSubscription (sub) {
      sub.errors.clear()

      api
        .delete(`/videoettes/subscriptions/${sub.subscription.id}`)
        .then(response => {
          this.getSubscription()
        })
        .catch(({ response }) => {
          switch (response.status) {
            case 403:
              this.displayToastNotification('You do not have permission to deactivate subscriptions for this office.')
              return
            case 422:
              sub.errors.store(response.data)
              return
          }

          this.displayToastNotification("We've been unable to deactivate your subscription at this time, please try again later.")
        })
    }
  },

  watch: {
    selectedOffice () {
      this.getSubscription()
    }
  },

  mounted () {
    if (!this.selectedOffice && !this.selectedOffice.id) return

    this.getSubscription()
  }
}
</script>
